import {
	assertAuthTokenSet,
	AuthStorageCorruptError,
	AuthStorageTransientError,
	AuthTokenRevision,
	AuthTokenSet,
	isStoredAuthTokens,
	sameRevision,
	StoredAuthTokens,
} from './types'
import { AuthTokenStorageConfig } from './config'

export const AUTH_META_STORE = 'meta'
export const AUTH_KEY_STORE = 'keys'
export const AUTH_ENVELOPE_STORE = 'envelopes'
export const AUTH_META_ID = 'auth-state'
export const AUTH_ENVELOPE_ID = 'tokens'

type VaultMeta = AuthTokenRevision & {
	id: typeof AUTH_META_ID
	schemaVersion: number
	tombstone: boolean
	keyId: string | null
	updatedAt: number
	legacyImportClosed?: boolean
	legacyCleanupComplete?: boolean
}

type EncryptedEnvelope = AuthTokenRevision & {
	id: typeof AUTH_ENVELOPE_ID
	schemaVersion: number
	keyId: string
	iv: ArrayBuffer
	ciphertext: ArrayBuffer
}

type StoredCryptoKey = {
	id: string
	key: CryptoKey
}

type EncryptedPayload = {
	schemaVersion: number
	tokens: StoredAuthTokens
}

type RawSnapshot = {
	meta: unknown
	envelope: unknown
	keys: unknown[]
	metaFingerprint: string
}

const MAX_COMMIT_RETRIES = 8
const AES_GCM_IV_BYTES = 12
const KEY_ID_BYTES = 16

const initialMeta = (config: AuthTokenStorageConfig, now: number): VaultMeta => ({
	id: AUTH_META_ID,
	schemaVersion: config.schemaVersion,
	generation: 0,
	revision: 0,
	tombstone: true,
	keyId: null,
	updatedAt: now,
	legacyImportClosed: false,
	legacyCleanupComplete: false,
})

const isObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null

const isRevisionPart = (value: unknown): value is number =>
	typeof value === 'number' && Number.isSafeInteger(value) && value >= 0

const isVaultMeta = (value: unknown, schemaVersion: number): value is VaultMeta => {
	if (!isObject(value)) return false
	return (
		value.id === AUTH_META_ID &&
		value.schemaVersion === schemaVersion &&
		isRevisionPart(value.generation) &&
		isRevisionPart(value.revision) &&
		typeof value.tombstone === 'boolean' &&
		(value.keyId === null || typeof value.keyId === 'string') &&
		typeof value.updatedAt === 'number' &&
		Number.isFinite(value.updatedAt) &&
		value.updatedAt >= 0 &&
		(value.legacyImportClosed === undefined || typeof value.legacyImportClosed === 'boolean') &&
		(value.legacyCleanupComplete === undefined ||
			typeof value.legacyCleanupComplete === 'boolean') &&
		(value.tombstone ? value.keyId === null : Boolean(value.keyId))
	)
}

const toArrayBuffer = (value: unknown): ArrayBuffer | null => {
	if (value instanceof ArrayBuffer) return value
	if (
		Object.prototype.toString.call(value) === '[object ArrayBuffer]' &&
		isObject(value) &&
		typeof value.byteLength === 'number'
	) {
		return value as unknown as ArrayBuffer
	}
	if (ArrayBuffer.isView(value)) {
		return value.buffer.slice(
			value.byteOffset,
			value.byteOffset + value.byteLength
		) as ArrayBuffer
	}
	return null
}

const parseEnvelope = (value: unknown, schemaVersion: number): EncryptedEnvelope | null => {
	if (!isObject(value)) return null
	const iv = toArrayBuffer(value.iv)
	const ciphertext = toArrayBuffer(value.ciphertext)
	if (
		value.id !== AUTH_ENVELOPE_ID ||
		value.schemaVersion !== schemaVersion ||
		typeof value.keyId !== 'string' ||
		!value.keyId ||
		!isRevisionPart(value.generation) ||
		!isRevisionPart(value.revision) ||
		!iv ||
		iv.byteLength !== AES_GCM_IV_BYTES ||
		!ciphertext ||
		ciphertext.byteLength <= 16
	) {
		return null
	}

	return {
		id: AUTH_ENVELOPE_ID,
		schemaVersion,
		keyId: value.keyId,
		generation: value.generation,
		revision: value.revision,
		iv,
		ciphertext,
	}
}

const isUsableCryptoKey = (value: unknown): value is CryptoKey => {
	if (!isObject(value)) return false
	const algorithm = value.algorithm
	const usages = value.usages
	return (
		value.type === 'secret' &&
		value.extractable === false &&
		isObject(algorithm) &&
		algorithm.name === 'AES-GCM' &&
		Array.isArray(usages) &&
		usages.includes('encrypt') &&
		usages.includes('decrypt')
	)
}

const parseStoredKey = (value: unknown): StoredCryptoKey | null => {
	if (!isObject(value) || typeof value.id !== 'string' || !isUsableCryptoKey(value.key)) {
		return null
	}
	return { id: value.id, key: value.key }
}

const metaFingerprint = (value: unknown): string => {
	try {
		return JSON.stringify(value)
	} catch {
		return '__unserializable__'
	}
}

const sameMeta = (left: VaultMeta, right: VaultMeta): boolean =>
	left.schemaVersion === right.schemaVersion &&
	left.tombstone === right.tombstone &&
	left.keyId === right.keyId &&
	(left.legacyImportClosed === true) === (right.legacyImportClosed === true) &&
	(left.legacyCleanupComplete === true) === (right.legacyCleanupComplete === true) &&
	sameRevision(left, right)

const requestResult = <T>(request: IDBRequest<T>): Promise<T> =>
	new Promise<T>((resolve, reject) => {
		request.onsuccess = () => resolve(request.result)
		request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'))
	})

const transactionDone = (transaction: IDBTransaction): Promise<void> =>
	new Promise<void>((resolve, reject) => {
		transaction.oncomplete = () => resolve()
		transaction.onabort = () =>
			reject(
				transaction.error ?? new DOMException('IndexedDB transaction aborted', 'AbortError')
			)
		transaction.onerror = () => {
			// onabort is the terminal event and carries the final transaction error.
		}
	})

const asTransientError = (message: string, error: unknown): AuthStorageTransientError =>
	error instanceof AuthStorageTransientError
		? error
		: new AuthStorageTransientError(message, error)

const deriveGeneration = (value: unknown): number => {
	if (isObject(value) && isRevisionPart(value.generation)) return value.generation
	return 0
}

const randomHex = (crypto: Crypto, byteLength: number): string => {
	const bytes = crypto.getRandomValues(new Uint8Array(byteLength))
	return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
}

export const canonicalAuthTokenAad = (
	config: AuthTokenStorageConfig,
	keyId: string,
	revision: AuthTokenRevision
): Uint8Array =>
	new TextEncoder().encode(
		JSON.stringify({
			app: config.appId,
			issuer: config.issuer,
			client: config.clientId,
			schema: config.schemaVersion,
			keyId,
			generation: revision.generation,
			revision: revision.revision,
		})
	)

export type IndexedDbAuthVaultOptions = {
	config: AuthTokenStorageConfig
	indexedDB: IDBFactory
	crypto: Crypto
	now: () => number
	onObserve: (revision: AuthTokenRevision) => void
	onChange: (type: 'tokens-updated' | 'logout', revision: AuthTokenRevision) => void
}

export class IndexedDbAuthVault {
	private databasePromise: Promise<IDBDatabase> | null = null
	private activeDatabase: IDBDatabase | null = null

	constructor(private readonly options: IndexedDbAuthVaultOptions) {}

	private async openDatabase(): Promise<IDBDatabase> {
		if (this.databasePromise) return this.databasePromise

		this.databasePromise = new Promise<IDBDatabase>((resolve, reject) => {
			let request: IDBOpenDBRequest
			try {
				request = this.options.indexedDB.open(
					this.options.config.databaseName,
					this.options.config.databaseVersion
				)
			} catch (error) {
				reject(asTransientError('Unable to open the auth token database', error))
				return
			}

			request.onupgradeneeded = event => {
				const database = request.result
				const transaction = request.transaction
				if (!transaction) return

				const metaStore = database.objectStoreNames.contains(AUTH_META_STORE)
					? transaction.objectStore(AUTH_META_STORE)
					: database.createObjectStore(AUTH_META_STORE, { keyPath: 'id' })
				if (!database.objectStoreNames.contains(AUTH_KEY_STORE)) {
					database.createObjectStore(AUTH_KEY_STORE, { keyPath: 'id' })
				}
				if (!database.objectStoreNames.contains(AUTH_ENVELOPE_STORE)) {
					database.createObjectStore(AUTH_ENVELOPE_STORE, { keyPath: 'id' })
				}

				if (event.oldVersion === 0) {
					metaStore.put(initialMeta(this.options.config, this.options.now()))
				}
			}
			request.onsuccess = () => {
				const database = request.result
				this.activeDatabase = database
				const releaseCachedDatabase = (): void => {
					if (this.activeDatabase === database) {
						this.activeDatabase = null
						this.databasePromise = null
					}
				}
				database.onversionchange = () => {
					database.close()
					releaseCachedDatabase()
				}
				database.onclose = releaseCachedDatabase
				resolve(database)
			}
			request.onerror = () =>
				reject(asTransientError('Unable to open the auth token database', request.error))
			request.onblocked = () =>
				reject(new AuthStorageTransientError('Auth token database upgrade is blocked'))
		}).catch(error => {
			this.databasePromise = null
			throw error
		})

		return this.databasePromise
	}

	private async readRawSnapshot(): Promise<RawSnapshot> {
		try {
			const database = await this.openDatabase()
			const transaction = database.transaction(
				[AUTH_META_STORE, AUTH_ENVELOPE_STORE, AUTH_KEY_STORE],
				'readonly'
			)
			const completed = transactionDone(transaction)
			const metaRequest = transaction.objectStore(AUTH_META_STORE).get(AUTH_META_ID)
			const envelopeRequest = transaction
				.objectStore(AUTH_ENVELOPE_STORE)
				.get(AUTH_ENVELOPE_ID)
			const keysRequest = transaction.objectStore(AUTH_KEY_STORE).getAll()
			const [meta, envelope, keys] = await Promise.all([
				requestResult(metaRequest),
				requestResult(envelopeRequest),
				requestResult(keysRequest),
			])
			await completed
			return { meta, envelope, keys, metaFingerprint: metaFingerprint(meta) }
		} catch (error) {
			throw asTransientError('Unable to read the auth token database', error)
		}
	}

	private async readCurrentMeta(): Promise<unknown> {
		try {
			const database = await this.openDatabase()
			const transaction = database.transaction(AUTH_META_STORE, 'readonly')
			const completed = transactionDone(transaction)
			const meta = await requestResult(
				transaction.objectStore(AUTH_META_STORE).get(AUTH_META_ID)
			)
			await completed
			return meta
		} catch (error) {
			throw asTransientError('Unable to verify auth token metadata', error)
		}
	}

	private async encrypt(
		tokens: StoredAuthTokens,
		key: CryptoKey,
		keyId: string
	): Promise<EncryptedEnvelope> {
		const iv = this.options.crypto.getRandomValues(new Uint8Array(AES_GCM_IV_BYTES))
		const aad = canonicalAuthTokenAad(this.options.config, keyId, tokens)
		const plaintext = new TextEncoder().encode(
			JSON.stringify({
				schemaVersion: this.options.config.schemaVersion,
				tokens,
			} satisfies EncryptedPayload)
		)
		let ciphertext: ArrayBuffer
		try {
			ciphertext = await this.options.crypto.subtle.encrypt(
				{ name: 'AES-GCM', iv, additionalData: aad, tagLength: 128 },
				key,
				plaintext
			)
		} catch (error) {
			throw new AuthStorageCorruptError('Unable to encrypt auth tokens', error)
		}

		return {
			id: AUTH_ENVELOPE_ID,
			schemaVersion: this.options.config.schemaVersion,
			keyId,
			generation: tokens.generation,
			revision: tokens.revision,
			iv: iv.buffer.slice(iv.byteOffset, iv.byteOffset + iv.byteLength) as ArrayBuffer,
			ciphertext,
		}
	}

	private async decrypt(envelope: EncryptedEnvelope, key: CryptoKey): Promise<StoredAuthTokens> {
		const aad = canonicalAuthTokenAad(this.options.config, envelope.keyId, envelope)
		try {
			const plaintext = await this.options.crypto.subtle.decrypt(
				{
					name: 'AES-GCM',
					iv: envelope.iv,
					additionalData: aad,
					tagLength: 128,
				},
				key,
				envelope.ciphertext
			)
			const parsed: unknown = JSON.parse(
				new TextDecoder('utf-8', { fatal: true }).decode(plaintext)
			)
			if (
				!isObject(parsed) ||
				parsed.schemaVersion !== this.options.config.schemaVersion ||
				!isStoredAuthTokens(parsed.tokens)
			) {
				throw new AuthStorageCorruptError('Auth token payload has an invalid schema')
			}
			return parsed.tokens
		} catch (error) {
			if (error instanceof AuthStorageCorruptError) throw error
			throw new AuthStorageCorruptError('Unable to decrypt auth tokens', error)
		}
	}

	private async clearCorruptSnapshot(snapshot: RawSnapshot): Promise<boolean> {
		try {
			const database = await this.openDatabase()
			const transaction = database.transaction(
				[AUTH_META_STORE, AUTH_ENVELOPE_STORE, AUTH_KEY_STORE],
				'readwrite'
			)
			const completed = transactionDone(transaction)
			const metaStore = transaction.objectStore(AUTH_META_STORE)
			const currentMeta = await requestResult(metaStore.get(AUTH_META_ID))
			if (metaFingerprint(currentMeta) !== snapshot.metaFingerprint) {
				await completed
				return false
			}

			const nextRevision = {
				generation: deriveGeneration(currentMeta) + 1,
				revision: 0,
			}
			transaction.objectStore(AUTH_KEY_STORE).clear()
			transaction.objectStore(AUTH_ENVELOPE_STORE).clear()
			metaStore.put({
				...initialMeta(this.options.config, this.options.now()),
				...nextRevision,
				legacyImportClosed:
					isVaultMeta(currentMeta, this.options.config.schemaVersion) &&
					currentMeta.legacyImportClosed === true,
				legacyCleanupComplete:
					isVaultMeta(currentMeta, this.options.config.schemaVersion) &&
					currentMeta.legacyCleanupComplete === true,
			})
			await completed
			this.options.onChange('logout', nextRevision)
			return true
		} catch (error) {
			throw asTransientError('Unable to clear corrupt auth token data', error)
		}
	}

	private validateActiveSnapshot(snapshot: RawSnapshot): {
		meta: VaultMeta
		envelope: EncryptedEnvelope
		key: CryptoKey
	} {
		if (!isVaultMeta(snapshot.meta, this.options.config.schemaVersion)) {
			throw new AuthStorageCorruptError('Auth token metadata has an invalid schema')
		}
		if (snapshot.meta.tombstone) {
			throw new AuthStorageCorruptError('Auth token tombstone has active token data')
		}

		const envelope = parseEnvelope(snapshot.envelope, this.options.config.schemaVersion)
		if (
			!envelope ||
			envelope.keyId !== snapshot.meta.keyId ||
			!sameRevision(envelope, snapshot.meta)
		) {
			throw new AuthStorageCorruptError('Auth token envelope does not match its metadata')
		}

		const storedKey = snapshot.keys
			.map(parseStoredKey)
			.find((candidate): candidate is StoredCryptoKey => candidate?.id === envelope.keyId)
		if (!storedKey) {
			throw new AuthStorageCorruptError('Auth token encryption key is missing or invalid')
		}

		return { meta: snapshot.meta, envelope, key: storedKey.key }
	}

	public async getTokens(): Promise<StoredAuthTokens | null> {
		for (let attempt = 0; attempt < MAX_COMMIT_RETRIES; attempt += 1) {
			const snapshot = await this.readRawSnapshot()
			if (
				isVaultMeta(snapshot.meta, this.options.config.schemaVersion) &&
				snapshot.meta.tombstone
			) {
				if (snapshot.envelope === undefined && snapshot.keys.length === 0) {
					const currentMeta = await this.readCurrentMeta()
					if (
						isVaultMeta(currentMeta, this.options.config.schemaVersion) &&
						sameMeta(currentMeta, snapshot.meta)
					) {
						this.options.onObserve(snapshot.meta)
						return null
					}
					continue
				}
			}

			try {
				const { meta, envelope, key } = this.validateActiveSnapshot(snapshot)
				const tokens = await this.decrypt(envelope, key)
				if (
					!sameRevision(tokens, meta) ||
					tokens.generation !== envelope.generation ||
					tokens.revision !== envelope.revision
				) {
					throw new AuthStorageCorruptError('Auth token revision is inconsistent')
				}
				const currentMeta = await this.readCurrentMeta()
				if (
					!isVaultMeta(currentMeta, this.options.config.schemaVersion) ||
					!sameMeta(currentMeta, meta)
				) {
					continue
				}
				this.options.onObserve(tokens)
				return tokens
			} catch (error) {
				if (!(error instanceof AuthStorageCorruptError)) throw error
				const cleared = await this.clearCorruptSnapshot(snapshot)
				if (cleared) return null
			}
		}

		throw new AuthStorageTransientError('Auth token data changed repeatedly while loading')
	}

	public async getLegacyMigrationState(): Promise<{
		importClosed: boolean
		cleanupComplete: boolean
	}> {
		const meta = await this.readCurrentMeta()
		if (!isVaultMeta(meta, this.options.config.schemaVersion)) {
			throw new AuthStorageCorruptError('Auth token metadata has an invalid schema')
		}
		return {
			importClosed: meta.legacyImportClosed === true,
			cleanupComplete: meta.legacyCleanupComplete === true,
		}
	}

	public async markLegacyImportClosed(): Promise<void> {
		await this.updateLegacyMigrationState({ importClosed: true })
	}

	public async markLegacyCleanupComplete(): Promise<void> {
		await this.updateLegacyMigrationState({
			importClosed: true,
			cleanupComplete: true,
		})
	}

	private async updateLegacyMigrationState(update: {
		importClosed: true
		cleanupComplete?: true
	}): Promise<void> {
		try {
			const database = await this.openDatabase()
			const transaction = database.transaction(AUTH_META_STORE, 'readwrite')
			const completed = transactionDone(transaction)
			const metaStore = transaction.objectStore(AUTH_META_STORE)
			const currentMeta = await requestResult(metaStore.get(AUTH_META_ID))
			if (!isVaultMeta(currentMeta, this.options.config.schemaVersion)) {
				transaction.abort()
				await completed
				return
			}
			if (
				currentMeta.legacyImportClosed !== true ||
				(update.cleanupComplete === true && currentMeta.legacyCleanupComplete !== true)
			) {
				metaStore.put({
					...currentMeta,
					legacyImportClosed: true,
					legacyCleanupComplete:
						update.cleanupComplete === true ||
						currentMeta.legacyCleanupComplete === true,
					updatedAt: this.options.now(),
				} satisfies VaultMeta)
			}
			await completed
		} catch (error) {
			throw asTransientError('Unable to update legacy auth migration state', error)
		}
	}

	private async generateKey(): Promise<{ keyId: string; key: CryptoKey }> {
		try {
			const key = await this.options.crypto.subtle.generateKey(
				{ name: 'AES-GCM', length: 256 },
				false,
				['encrypt', 'decrypt']
			)
			if (!isUsableCryptoKey(key)) {
				throw new Error('Generated key is not a non-extractable AES-GCM key')
			}
			return { keyId: randomHex(this.options.crypto, KEY_ID_BYTES), key }
		} catch (error) {
			throw new AuthStorageCorruptError(
				'Unable to generate the auth token encryption key',
				error
			)
		}
	}

	public async saveNewSession(tokens: AuthTokenSet): Promise<StoredAuthTokens> {
		assertAuthTokenSet(tokens)

		for (let attempt = 0; attempt < MAX_COMMIT_RETRIES; attempt += 1) {
			const snapshot = await this.readRawSnapshot()
			if (!isVaultMeta(snapshot.meta, this.options.config.schemaVersion)) {
				await this.clearCorruptSnapshot(snapshot)
				continue
			}

			const nextTokens: StoredAuthTokens = {
				...tokens,
				storedAt: this.options.now(),
				generation: snapshot.meta.generation + 1,
				revision: 1,
			}
			const { keyId, key } = await this.generateKey()
			const envelope = await this.encrypt(nextTokens, key, keyId)
			const committed = await this.commitNewSession(snapshot.meta, envelope, key)
			if (committed) {
				this.options.onChange('tokens-updated', nextTokens)
				return nextTokens
			}
		}

		throw new AuthStorageTransientError(
			'Unable to commit a new auth session after concurrent updates'
		)
	}

	public async saveNewSessionIfCurrent(
		expected: AuthTokenRevision,
		tokens: AuthTokenSet
	): Promise<StoredAuthTokens | null> {
		assertAuthTokenSet(tokens)
		const snapshot = await this.readRawSnapshot()
		if (!isVaultMeta(snapshot.meta, this.options.config.schemaVersion)) {
			await this.clearCorruptSnapshot(snapshot)
			return null
		}
		if (!sameRevision(snapshot.meta, expected)) return null

		const nextTokens: StoredAuthTokens = {
			...tokens,
			storedAt: this.options.now(),
			generation: snapshot.meta.generation + 1,
			revision: 1,
		}
		const { keyId, key } = await this.generateKey()
		const envelope = await this.encrypt(nextTokens, key, keyId)
		const committed = await this.commitNewSession(snapshot.meta, envelope, key)
		if (!committed) return null

		this.options.onChange('tokens-updated', nextTokens)
		return nextTokens
	}

	private async commitNewSession(
		expectedMeta: VaultMeta,
		envelope: EncryptedEnvelope,
		key: CryptoKey
	): Promise<boolean> {
		try {
			const database = await this.openDatabase()
			const transaction = database.transaction(
				[AUTH_META_STORE, AUTH_ENVELOPE_STORE, AUTH_KEY_STORE],
				'readwrite'
			)
			const completed = transactionDone(transaction)
			const metaStore = transaction.objectStore(AUTH_META_STORE)
			const currentMeta = await requestResult(metaStore.get(AUTH_META_ID))
			if (
				!isVaultMeta(currentMeta, this.options.config.schemaVersion) ||
				!sameMeta(currentMeta, expectedMeta)
			) {
				await completed
				return false
			}

			const keyStore = transaction.objectStore(AUTH_KEY_STORE)
			keyStore.clear()
			keyStore.put({ id: envelope.keyId, key } satisfies StoredCryptoKey)
			const envelopeStore = transaction.objectStore(AUTH_ENVELOPE_STORE)
			envelopeStore.clear()
			envelopeStore.put(envelope)
			metaStore.put({
				id: AUTH_META_ID,
				schemaVersion: this.options.config.schemaVersion,
				generation: envelope.generation,
				revision: envelope.revision,
				tombstone: false,
				keyId: envelope.keyId,
				updatedAt: this.options.now(),
				legacyImportClosed: expectedMeta.legacyImportClosed === true,
				legacyCleanupComplete: expectedMeta.legacyCleanupComplete === true,
			} satisfies VaultMeta)
			await completed
			return true
		} catch (error) {
			throw asTransientError('Unable to atomically save the auth session', error)
		}
	}

	public async compareAndSetRefresh(
		expected: AuthTokenRevision,
		tokens: AuthTokenSet
	): Promise<StoredAuthTokens | null> {
		assertAuthTokenSet(tokens)
		const snapshot = await this.readRawSnapshot()
		if (!isVaultMeta(snapshot.meta, this.options.config.schemaVersion)) {
			await this.clearCorruptSnapshot(snapshot)
			return null
		}
		if (snapshot.meta.tombstone || !sameRevision(snapshot.meta, expected)) return null

		let active: ReturnType<IndexedDbAuthVault['validateActiveSnapshot']>
		try {
			active = this.validateActiveSnapshot(snapshot)
			const currentTokens = await this.decrypt(active.envelope, active.key)
			if (!sameRevision(currentTokens, expected)) {
				throw new AuthStorageCorruptError(
					'Stored auth tokens do not match the expected revision'
				)
			}
		} catch (error) {
			if (!(error instanceof AuthStorageCorruptError)) throw error
			await this.clearCorruptSnapshot(snapshot)
			return null
		}

		const nextTokens: StoredAuthTokens = {
			...tokens,
			storedAt: this.options.now(),
			generation: expected.generation,
			revision: expected.revision + 1,
		}
		const envelope = await this.encrypt(nextTokens, active.key, active.envelope.keyId)
		const committed = await this.commitRefresh(expected, active.envelope.keyId, envelope)
		if (!committed) return null

		this.options.onChange('tokens-updated', nextTokens)
		return nextTokens
	}

	private async commitRefresh(
		expected: AuthTokenRevision,
		expectedKeyId: string,
		envelope: EncryptedEnvelope
	): Promise<boolean> {
		try {
			const database = await this.openDatabase()
			const transaction = database.transaction(
				[AUTH_META_STORE, AUTH_ENVELOPE_STORE],
				'readwrite'
			)
			const completed = transactionDone(transaction)
			const metaStore = transaction.objectStore(AUTH_META_STORE)
			const currentMeta = await requestResult(metaStore.get(AUTH_META_ID))
			if (
				!isVaultMeta(currentMeta, this.options.config.schemaVersion) ||
				currentMeta.tombstone ||
				currentMeta.keyId !== expectedKeyId ||
				!sameRevision(currentMeta, expected)
			) {
				await completed
				return false
			}

			transaction.objectStore(AUTH_ENVELOPE_STORE).put(envelope)
			metaStore.put({
				...currentMeta,
				revision: envelope.revision,
				updatedAt: this.options.now(),
			} satisfies VaultMeta)
			await completed
			return true
		} catch (error) {
			throw asTransientError('Unable to atomically update refreshed auth tokens', error)
		}
	}

	public async clear(
		expected?: AuthTokenRevision | { generation: number }
	): Promise<AuthTokenRevision | null> {
		try {
			const database = await this.openDatabase()
			const transaction = database.transaction(
				[AUTH_META_STORE, AUTH_ENVELOPE_STORE, AUTH_KEY_STORE],
				'readwrite'
			)
			const completed = transactionDone(transaction)
			const metaStore = transaction.objectStore(AUTH_META_STORE)
			const currentRaw = await requestResult(metaStore.get(AUTH_META_ID))
			const currentMeta = isVaultMeta(currentRaw, this.options.config.schemaVersion)
				? currentRaw
				: null

			if (expected) {
				if (!currentMeta || currentMeta.generation !== expected.generation) {
					await completed
					return null
				}
				if ('revision' in expected && currentMeta.revision !== expected.revision) {
					await completed
					return null
				}
			}

			const nextRevision = {
				generation: (currentMeta?.generation ?? deriveGeneration(currentRaw)) + 1,
				revision: 0,
			}
			transaction.objectStore(AUTH_KEY_STORE).clear()
			transaction.objectStore(AUTH_ENVELOPE_STORE).clear()
			metaStore.put({
				...initialMeta(this.options.config, this.options.now()),
				...nextRevision,
				legacyImportClosed: currentMeta?.legacyImportClosed === true,
				legacyCleanupComplete: currentMeta?.legacyCleanupComplete === true,
			})
			await completed
			this.options.onChange('logout', nextRevision)
			return nextRevision
		} catch (error) {
			throw asTransientError('Unable to atomically clear auth tokens', error)
		}
	}

	public close(): void {
		this.activeDatabase?.close()
		this.activeDatabase = null
		this.databasePromise = null
	}
}
